import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  PersistConfig,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist";
import { Reducer, combineReducers, configureStore } from "@reduxjs/toolkit";

import { apiSlice } from "./api/apiSlice";
import authReducer from "./auth/authSlice";
import issuesReducer from "./appState/issuesSlice";
import { rtkQueryIssueLogger } from "./middleware";
import { setupListeners } from "@reduxjs/toolkit/query";
import storage from "redux-persist/es/storage";
import { useDispatch } from "react-redux";
import userPrefsReducer from "./appState/userPrefsSlice";
import { v4 as uuidv4 } from "uuid";
import uxReducer from "./appState/uxSlice";

const reducers = {
  [apiSlice.reducerPath]: apiSlice.reducer,
  auth: authReducer,
  issues: issuesReducer,
  userPrefs: userPrefsReducer,
  ux: uxReducer,
};

const rootReducer = combineReducers(reducers);

export const CURRENT_VERSION = 1;

// Migration example if data format changes
/*
const migration = {
  [CURRENT_VERSION]: (state: RootState) => {
     return {
        ...state,
        newPropContainer: {
           ...state.oldPropContainer,
           newProp: 0
        }
     }
  }
}
*/

type InferState<Type> = Type extends Reducer<infer S> ? S : never;
type State<
  T extends typeof reducers = typeof reducers,
  P extends keyof T = keyof T
> = {
  [K in P]: InferState<T[K]>;
};

const persistConfig: PersistConfig<State> = {
  version: CURRENT_VERSION,
  key: "root",
  storage,
  blacklist: [apiSlice.reducerPath, "ux"],
  //migrate: createMigrate(migration, { debug: __DEV__ })
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Redux persist
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(apiSlice.middleware, rtkQueryIssueLogger),
});

const persistor = persistStore(store);

// Allow RTK Query to know about tab focus and network state
setupListeners(store.dispatch);

// Compute tab instance ID
export const tabId =
  !!sessionStorage.tabId && sessionStorage.closedLastTab !== "2"
    ? sessionStorage.tabId
    : (sessionStorage.tabId = uuidv4());
sessionStorage.closedLastTab = "2";
window.addEventListener("beforeunload", () => {
  sessionStorage.closedLastTab = "1";
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;

export { store, persistor };
